import { create } from 'zustand';
import { Modal } from './Modal';

export type ConfirmVariant = 'danger' | 'primary' | 'accent';

export interface ConfirmOptions {
  title: string;
  icon?: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmVariant;
}

interface ConfirmState extends ConfirmOptions {
  id: number;
  resolve: (ok: boolean) => void;
}

interface ConfirmStore {
  current: ConfirmState | null;
  ask: (opts: ConfirmOptions) => Promise<boolean>;
  resolveCurrent: (ok: boolean) => void;
}

let seq = 1;

const useConfirmStore = create<ConfirmStore>((set, get) => ({
  current: null,
  ask: (opts) =>
    new Promise<boolean>((resolve) => {
      set({ current: { id: seq++, resolve, ...opts } });
    }),
  resolveCurrent: (ok) => {
    const c = get().current;
    if (!c) return;
    c.resolve(ok);
    set({ current: null });
  },
}));

/**
 * Global themed confirm. Use in place of window.confirm():
 *   if (await confirm({ title, message })) doDangerousThing();
 */
export async function confirm(opts: ConfirmOptions): Promise<boolean> {
  return useConfirmStore.getState().ask(opts);
}

export function ConfirmDialogHost(): React.ReactElement | null {
  const current = useConfirmStore((s) => s.current);
  const resolveCurrent = useConfirmStore((s) => s.resolveCurrent);
  if (!current) return null;

  const variant = current.variant ?? 'primary';
  const btnClass =
    variant === 'danger'
      ? 'sl-btn sl-btn-danger'
      : variant === 'accent'
        ? 'sl-btn sl-btn-accent'
        : 'sl-btn sl-btn-primary';

  return (
    <Modal
      title={current.title}
      icon={current.icon ?? '❓'}
      onClose={() => resolveCurrent(false)}
    >
      <div style={{ marginBottom: 16 }}>{current.message}</div>
      <div
        className="sl-modal-footer"
        style={{ padding: 0, border: 'none', marginTop: 12 }}
      >
        <button type="button" className="sl-btn sl-btn-ghost" onClick={() => resolveCurrent(false)}>
          {current.cancelLabel ?? 'Cancel'}
        </button>
        <button
          type="button"
          className={btnClass}
          autoFocus
          onClick={() => resolveCurrent(true)}
        >
          {current.confirmLabel ?? 'OK'}
        </button>
      </div>
    </Modal>
  );
}
